import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import copy from "copy-to-clipboard";
import styled from "styled-components/macro";
import { Box, CircularProgress, Paper, Typography } from "@material-ui/core";

import Map from "./map";
import MapIFrame from "./map/MapIFrame";
import WellStylesControl from "./controls/wellStylesControl";
import Search from "./filters/search";
import FilterControl from "./filters/filterControl";
import Filter from "./filters/filter";

import { useMap } from "./hooks/useMap";
import useFilters from "./hooks/useFilters";
import {
  GRAPH_MODE,
  MAP_EXPLORER_MODE,
  getPlainFilterValues,
  keepParametersInGroups,
} from "./hooks/useFilters/filterDefaults";
import useLayerStyles from "./hooks/useLayerStyles";
import { BASEMAP_STYLES, INIT_MAP_CONFIG } from "./constants";
import { useLocation } from "react-router-dom";
import {
  getDefaultLayerVisibility,
  getDefaultOpacity,
  getLayerGroupKey,
  readRawMapUrl,
  resolveFilterState,
  resolveLayerState,
  resolveLocationId,
} from "./urlState";
import {
  useDroppedUrlNotice,
  useInitialMapUrl,
  useMapUrlWriter,
} from "./urlState/useMapUrlState";
import ShareMapControls from "./components/ShareMapControls";
import { ROUTES } from "../../constants";
import { useApp } from "../../AppProvider";

import DisclaimerDialog from "./components/DisclaimerDialog";
import MainControl from "./controls/mainControl/";

import PrintReportDialog from "./components/PrintReportDialog";
import { useReactToPrint } from "react-to-print";
import PrintMapFormat from "./components/PrintMapFormat";
import SplitButton from "../../components/SplitButton";
import GraphModeToggle from "./controls/graphModeToggle";
import DataViz from "./components/DataViz";
import { useWaterQualitySummary } from "./hooks/useWaterQualitySummary";
import { useWaterQualityTimeSeries } from "./hooks/useWaterQualityTimeSeries";
import { useBenchmarkScaleColors } from "./hooks/useBenchmarkScaleColors";
import LegendControl from "./controls/legendControl";
import Legend from "./components/Legend";
import PeriodOfRecordInputs from "./components/periodOfRecordInputs";
import StreamflowDataDialog from "./components/StreamflowDataDialog";
import useFetchGraphData from "../../hooks/useFetchGraphData";

const FiltersBar = styled(Paper)`
  align-items: center;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)}px;
  /* no right padding: the pinned actions own that edge (see ActionsSection) */
  padding: 12px 0 12px 32px;
  /* one row of controls: it scrolls sideways when they do not fit */
  overflow-x: auto;
  overflow-y: hidden;
`;

const FiltersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)}px;
`;

const FiltersSectionRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing(2)}px;
  flex-grow: 100;
`;

const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)}px;
  flex: 1 1 0;
`;

/**
 * The bar scrolls sideways when the filters do not fit (common in the water
 * quality view on smaller screens), so the actions stay pinned to the right
 * edge instead of scrolling out of reach
 */
/**
 * The bar scrolls sideways when the filters do not fit (common in the water
 * quality view on smaller screens), so the link actions stay pinned to the
 * right edge instead of scrolling out of reach
 */
const ActionsSection = styled(FiltersSection)`
  position: sticky;
  right: 0;
  z-index: 2;
  align-self: stretch;
  justify-content: center;
  /* stretches over the bar's vertical padding so the rest of the bar scrolls
     underneath it without showing through */
  margin: -12px 0;
  padding: 12px 16px 12px 24px;
  background: ${({ theme }) => theme.palette.background.paper};
  box-shadow: -10px 0 10px -8px rgba(0, 0, 0, 0.2),
    10px 0 10px -8px rgba(0, 0, 0, 0.2);
`;

// sits to the left of the pinned actions, which carry the bar's right padding
const PrintSection = styled(FiltersSection)`
  padding-right: 0;
`;

const GraphModeFilterContainer = styled.div`
  width: 125px;
`;

const LocationsLoading = styled(Paper)`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)}px;
  left: 50%;
  padding: 6px 12px;
  position: absolute;
  top: 10px;
  transform: translateX(-50%);
  z-index: 2;
`;

const getMoreFiltersCount = (filterValues) => {
  const keys = ["hasTelem", "isActive", "isCcbwqa"];
  return keys.filter((key) => filterValues[key].value).length;
};

/**
 * The interactive map, shared by the map page and the embeddable iframe
 * @param {string} variant "page" | "iframe"
 */
const PublicMapContent = ({ variant = "page" }) => {
  const isIFrame = variant === "iframe";
  const MapContainer = isIFrame ? MapIFrame : Map;
  const mapContainer = useRef(null);
  const { doToast } = useApp();
  const { search: routerSearch } = useLocation();

  // the URL is read once on mount, every section below hydrates from it
  const {
    raw: urlState,
    getDropped: getDroppedUrlValues,
    reportDropped,
  } = useInitialMapUrl();
  const isGraphModeFromUrl = urlState.mode === GRAPH_MODE;

  const [mode, setMode] = useState(urlState.mode);
  const [resultsPanelVisible, setResultsPanelVisible] = useState(
    isGraphModeFromUrl && urlState.resultsPanel !== false
  );
  const [legendVisible, setLegendVisible] = useState(
    isGraphModeFromUrl && urlState.legend !== false
  );
  // remounts the main control so its local state (sliders, accordions) resets
  const [mainControlKey, setMainControlKey] = useState(0);

  const { data: flowLocationsData } = useFetchGraphData(
    {},
    "streamflow-data/locations"
  );
  const flowLocationIds = useMemo(() => {
    if (!flowLocationsData?.length) return new Set();
    return new Set(flowLocationsData.map((loc) => loc.locationid));
  }, [flowLocationsData]);

  // the map is created with the basemap and camera from the URL, so a shared
  // link does not load the default view first
  const mapConfig = useMemo(() => {
    const basemap = BASEMAP_STYLES.find(
      (item) => item.style === urlState.basemap
    );
    const camera = urlState.camera;
    return {
      ...INIT_MAP_CONFIG,
      ...(basemap && { style: basemap.url }),
      ...(camera && {
        center: [camera.lng, camera.lat],
        zoom: camera.zoom,
        bearing: camera.bearing || 0,
        pitch: camera.pitch || 0,
      }),
    };
  }, []); //eslint-disable-line

  const applyLayerOverrides = useCallback(
    (layers) => {
      const { value, dropped } = resolveLayerState(urlState, layers);
      reportDropped(dropped);
      const defaultVisibility = getDefaultLayerVisibility(layers);

      return layers.map((layer) => {
        const groupKey = getLayerGroupKey(layer);
        const isVisibilityChanged =
          value.visibility[groupKey] !== defaultVisibility[groupKey];
        const opacity = value.opacity[layer.id];
        const hasOpacity =
          layer.type === "fill" &&
          typeof layer.paint?.["fill-opacity"] === "number" &&
          opacity !== undefined;

        if (!isVisibilityChanged && !hasOpacity) return layer;
        return {
          ...layer,
          layout: isVisibilityChanged
            ? {
                ...layer.layout,
                visibility: value.visibility[groupKey] ? "visible" : "none",
              }
            : layer.layout,
          paint: hasOpacity
            ? { ...layer.paint, "fill-opacity": opacity }
            : layer.paint,
        };
      });
    },
    [urlState, reportDropped]
  );

  const {
    activeBasemap,
    basemaps,
    layers,
    defaultLayers,
    map,
    sources,
    isMapReady,
    styleVersion,
    applyLayerFilters,
    applyLayerPaint,
    updateLayerVisibility,
    revealLocationLayers,
    areLocationsRevealed,
    resetLayers,
    updateBasemap,
    eventsRegistered,
    currentPapFeatures,
    updateLayerOpacity,
    selectedMonitoringLocation,
    setSelectedMonitoringLocation,
  } = useMap({
    ref: mapContainer,
    mapConfig,
    applyLayerOverrides,
    deferLocationReveal: true,
    initialBasemap: urlState.basemap,
    setResultsPanelVisible,
    mode,
    flowLocationIds,
  });

  const getInitialFilterValues = useCallback(
    (filtersData) => {
      const { value, dropped } = resolveFilterState(urlState, filtersData);
      reportDropped(dropped);
      const { colorBy, ...filterValues } = value;

      // in graph mode parameters must belong to a selected group
      if (isGraphModeFromUrl && urlState.parameterGroups !== undefined) {
        filterValues.parameters = keepParametersInGroups(
          filterValues.parameters,
          filterValues.parameterGroups,
          filtersData.parameters
        );
      }
      return filterValues;
    },
    [urlState, reportDropped, isGraphModeFromUrl]
  );

  const {
    filterValues,
    handleFilterValues,
    handleSelectAll,
    handleSelectNone,
    filtersData,
    isFiltersInitialized,
    isFiltersError,
    resetFiltersForMode,
    resetAllFilters,
  } = useFilters({ mode, getInitialValues: getInitialFilterValues });

  const {
    data: wqSummaryData,
    selectedLocationData: selectedLocationWqSummaryData,
    isLoading: isWqSummaryLoading,
    refetch: refetchWqSummary,
  } = useWaterQualitySummary({
    locationId: selectedMonitoringLocation?.locationid,
    parameterIds: filterValues?.parameters?.value,
    flow: filterValues?.flow?.value,
  });

  const { data: wqTimeSeriesData, isLoading: isWqTimeSeriesLoading } =
    useWaterQualityTimeSeries({
      locationId: selectedMonitoringLocation?.locationid,
      parameterIds: filterValues?.parameters?.value,
    });

  const { data: benchmarkScaleColors, isLoading: isBenchmarkColorsLoading } =
    useBenchmarkScaleColors();

  const {
    activeStyle,
    activeStyleId,
    handleActiveStyle,
    isStyleReady,
    styleOptions,
  } = useLayerStyles({
    analysis: filterValues?.analysis?.value,
    flow: filterValues?.flow?.value,
    parameters: filterValues?.parameters?.value,
    benchmarkScaleColors,
    benchmarkData: wqSummaryData,
    isBenchmarkDataLoading: isWqSummaryLoading,
    filtersData,
    initialStyleId:
      urlState.colorBy || (isGraphModeFromUrl ? "wqData" : "locationTypes"),
  });

  // keep the location layers filtered, including after a basemap change
  useEffect(() => {
    if (isMapReady && isFiltersInitialized) {
      applyLayerFilters(filterValues);
    }
  }, [
    filterValues,
    isMapReady,
    isFiltersInitialized,
    styleVersion,
    applyLayerFilters,
  ]);

  // keep the location layer painted, including after a basemap change
  useEffect(() => {
    if (isMapReady && isStyleReady) {
      applyLayerPaint(activeStyle);
    }
  }, [activeStyle, isMapReady, isStyleReady, styleVersion, applyLayerPaint]);

  /**
   * Reveal the locations once they are filtered and colored
   * Runs after the two effects above, so the first paint of the points is
   * already correct. A filters failure still reveals them, unfiltered
   */
  const isLocationsDataReady =
    isMapReady && (isFiltersInitialized || isFiltersError) && isStyleReady;
  useEffect(() => {
    if (isLocationsDataReady && !areLocationsRevealed) {
      revealLocationLayers();
    }
  }, [isLocationsDataReady, areLocationsRevealed, revealLocationLayers]);

  /**
   * Resolve the selected location from the URL once the locations source is
   * available. `undefined` means it has not been resolved yet
   */
  const [isLocationResolved, setIsLocationResolved] = useState(
    !isGraphModeFromUrl || urlState.locationId === undefined
  );
  useEffect(() => {
    if (isLocationResolved || !map) return;
    const features = sources.find((source) => source.id === "locations")?.data
      ?.features;
    if (!features) return;

    const locationIds = new Set(
      features.map((feature) => feature.properties?.locationid)
    );
    const { value: locationId, dropped } = resolveLocationId(
      urlState,
      locationIds
    );
    reportDropped(dropped);

    const feature = features.find(
      (item) => item.properties?.locationid === locationId
    );
    if (feature) {
      setSelectedMonitoringLocation(feature.properties);
      if (!urlState.camera) {
        map.jumpTo({ center: feature.geometry.coordinates, zoom: 13 });
      }
    }
    setIsLocationResolved(true);
  }, [map, sources, isLocationResolved]); //eslint-disable-line

  const isHydrated =
    isFiltersInitialized && layers.length > 0 && isLocationResolved;
  useDroppedUrlNotice(getDroppedUrlValues, isHydrated);

  const urlCurrentState = useMemo(
    () => ({
      mode,
      filters: isFiltersInitialized
        ? { ...getPlainFilterValues(filterValues), colorBy: activeStyleId }
        : undefined,
      locationId: isLocationResolved
        ? selectedMonitoringLocation?.locationid ?? null
        : undefined,
      resultsPanel: resultsPanelVisible,
      legend: legendVisible,
      layers: layers.length
        ? {
            visibility: getDefaultLayerVisibility(layers),
            opacity: getDefaultOpacity(layers),
          }
        : undefined,
      basemap: isMapReady ? activeBasemap : undefined,
    }),
    [
      mode,
      isFiltersInitialized,
      filterValues,
      activeStyleId,
      isLocationResolved,
      selectedMonitoringLocation,
      resultsPanelVisible,
      legendVisible,
      layers,
      isMapReady,
      activeBasemap,
    ]
  );
  const urlContext = useMemo(
    () => ({ filtersData, defaultLayers }),
    [filtersData, defaultLayers]
  );
  const buildUrlSearch = useMapUrlWriter({
    current: urlCurrentState,
    ctx: urlContext,
    map,
    // an embed's URL is not visible, so it only reads presets from its URL
    enabled: !isIFrame,
  });

  /**
   * Switches between the map explorer and graph mode
   * Resets the mode specific filters, style and panels
   * @param {string} nextMode "map-explorer" | "graph-mode"
   */
  const enterMode = (nextMode) => {
    const isGraphMode = nextMode === GRAPH_MODE;
    resetFiltersForMode(nextMode);
    handleActiveStyle(isGraphMode ? "wqData" : "locationTypes");
    setLegendVisible(isGraphMode);
    setResultsPanelVisible(isGraphMode);
    setSelectedMonitoringLocation(null);
    setMode(nextMode);
  };

  // a link to the current view, embeds link to the full map page
  const getShareUrl = () => {
    const pathname = isIFrame ? ROUTES.MAP : window.location.pathname;
    const search = buildUrlSearch(isIFrame ? "" : window.location.search);
    return `${window.location.origin}${pathname}${search ? `?${search}` : ""}`;
  };

  /**
   * The Map Explorer and Water Quality Map nav links point at this same page,
   * so switching between them changes the URL without remounting
   * This only reacts to real navigations: our own URL writes use replaceState
   * and never touch the router location
   */
  useEffect(() => {
    const navMode = readRawMapUrl(routerSearch).raw.mode;
    if (navMode !== mode) enterMode(navMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerSearch]);

  const handleCopyLink = () => {
    const isCopied = copy(getShareUrl());
    if (isCopied) {
      doToast(
        "success",
        "A link to this map view was copied to your clipboard."
      );
    } else {
      doToast("error", "The link could not be copied to your clipboard.");
    }
  };

  const handleReset = () => {
    if (!isFiltersInitialized) return;
    resetAllFilters(MAP_EXPLORER_MODE);
    handleActiveStyle("locationTypes");
    setLegendVisible(false);
    setResultsPanelVisible(false);
    setSelectedMonitoringLocation(null);
    setMode(MAP_EXPLORER_MODE);
    resetLayers();
    setMainControlKey((prevState) => prevState + 1);
    if (activeBasemap !== BASEMAP_STYLES[0].style) {
      updateBasemap(BASEMAP_STYLES[0]);
    }
    map?.flyTo({
      center: INIT_MAP_CONFIG.center,
      zoom: INIT_MAP_CONFIG.zoom,
      bearing: 0,
      pitch: 0,
    });
  };

  const handleSearchSelect = (result) => {
    map?.flyTo({ center: result?.location_geometry?.coordinates, zoom: 16 });
  };

  const printRef = useRef();
  const [printReportDialogOpen, setPrintReportDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const handlePrintMapClick = useReactToPrint({
    content: () => printRef.current,
  });

  const handleSavePNG = () => {
    const a = document.createElement("a");
    a.href = map.getCanvas().toDataURL();
    a.download = "map.png";
    document.body.appendChild(a);
    a.click();
  };

  const splitButtonOptions = ["Print PDF", "Save PNG"];
  const handleSplitButtonClick = (index) => {
    if (![0, 1].includes(index)) return;

    if (index === 0) {
      setPrintReportDialogOpen(true);
    } else if (index === 1) {
      handleSavePNG();
    }
  };

  return (
    <>
      {!isIFrame && process.env.NODE_ENV !== "development" && (
        <DisclaimerDialog />
      )}
      <FiltersBar>
        <FiltersSectionRow>
          <FiltersContainer>
            <Search onSelect={handleSearchSelect} />
          </FiltersContainer>
        </FiltersSectionRow>

        <FiltersSection>
          <FiltersContainer>
            <FilterControl
              width="132px"
              appliedCount={filterValues?.locationTypes?.value?.length}
              label="Loc Types"
            >
              <Filter
                label="Loc Types"
                name="locationTypes"
                onChange={handleFilterValues}
                onSelectAll={handleSelectAll}
                onSelectNone={handleSelectNone}
                options={filterValues?.locationTypes?.options}
                type={filterValues?.locationTypes?.type}
                value={filterValues?.locationTypes?.value}
              />
            </FilterControl>
            <FilterControl
              width="152px"
              appliedCount={filterValues?.parameterGroups?.value?.length}
              label="Param Groups"
            >
              <Filter
                label="Param Groups"
                name="parameterGroups"
                onChange={handleFilterValues}
                onSelectAll={handleSelectAll}
                onSelectNone={handleSelectNone}
                options={filterValues?.parameterGroups?.options}
                type={filterValues?.parameterGroups?.type}
                value={filterValues?.parameterGroups?.value}
              />
            </FilterControl>
            <FilterControl
              appliedCount={getMoreFiltersCount(filterValues)}
              label="More Filters"
              width="130px"
            >
              <Box display="flex" flexDirection="column">
                <Filter
                  label="Has Telemetry"
                  name="hasTelem"
                  onChange={handleFilterValues}
                  type="boolean"
                  value={filterValues?.hasTelem?.value}
                />
                <Filter
                  label="Is Active"
                  name="isActive"
                  onChange={handleFilterValues}
                  type="boolean"
                  value={filterValues?.isActive?.value}
                />
                <Filter
                  label="Is CCBWQA"
                  name="isCcbwqa"
                  onChange={handleFilterValues}
                  type="boolean"
                  value={filterValues?.isCcbwqa?.value}
                />
              </Box>
            </FilterControl>
          </FiltersContainer>
        </FiltersSection>

        {mode === GRAPH_MODE && (
          <FiltersSection>
            <FiltersContainer>
              <GraphModeFilterContainer>
                <Filter
                  label="Min Records"
                  name="min_records"
                  onChange={handleFilterValues}
                  type={filterValues?.min_records?.type}
                  value={filterValues?.min_records?.value}
                />
              </GraphModeFilterContainer>

              <GraphModeFilterContainer>
                <Filter
                  label="Analysis"
                  name="analysis"
                  onChange={handleFilterValues}
                  options={filterValues?.analysis?.options}
                  type={filterValues?.analysis?.type}
                  value={filterValues?.analysis?.value}
                />
              </GraphModeFilterContainer>

              <GraphModeFilterContainer>
                <Filter
                  label="Flow"
                  name="flow"
                  onChange={handleFilterValues}
                  options={filterValues?.flow?.options}
                  type={filterValues?.flow?.type}
                  value={filterValues?.flow?.value}
                />
              </GraphModeFilterContainer>

              <PeriodOfRecordInputs
                filterValues={filterValues}
                handleFilterValues={handleFilterValues}
                refetchWqSummary={refetchWqSummary}
              />
            </FiltersContainer>
          </FiltersSection>
        )}

        <FiltersSection>
          <FiltersContainer>
            <FilterControl width="185px" label={`Color by ${activeStyle.name}`}>
              <Typography variant="subtitle1" gutterBottom>
                Color Data Points by
              </Typography>
              <WellStylesControl
                mode={mode}
                label="Color Data Points by"
                name="wellStyles"
                onChange={handleActiveStyle}
                options={styleOptions}
                value={activeStyle.id}
              />
            </FilterControl>
          </FiltersContainer>
        </FiltersSection>

        <PrintSection>
          <FiltersContainer>
            <SplitButton
              options={splitButtonOptions}
              handleExportClick={handleSplitButtonClick}
            />
            <PrintReportDialog
              downloadCallback={handlePrintMapClick}
              setPrintReportDialogOpen={setPrintReportDialogOpen}
              printReportDialogOpen={printReportDialogOpen}
              title={title}
              setTitle={setTitle}
            />
          </FiltersContainer>
        </PrintSection>

        <ActionsSection>
          <ShareMapControls onCopyLink={handleCopyLink} onReset={handleReset} />
        </ActionsSection>
      </FiltersBar>
      <MapContainer ref={mapContainer}>
        {eventsRegistered && !areLocationsRevealed && (
          <LocationsLoading elevation={2}>
            <CircularProgress size={16} color="secondary" />
            <Typography variant="body2">
              Loading monitoring locations
            </Typography>
          </LocationsLoading>
        )}
        {eventsRegistered && (
          <MainControl
            key={mainControlKey}
            map={map}
            features={currentPapFeatures}
            activeBasemap={activeBasemap}
            basemaps={basemaps}
            filterValues={filterValues}
            handleFilterValues={handleFilterValues}
            onSelectAll={handleSelectAll}
            onSelectNone={handleSelectNone}
            layers={layers}
            onBasemapChange={updateBasemap}
            onLayerChange={updateLayerVisibility}
            onOpacityChange={updateLayerOpacity}
            mode={mode}
            defaultExpanded={mode === GRAPH_MODE ? "parameters" : "layers"}
            displayPAP={!isIFrame}
          />
        )}
        {mode === GRAPH_MODE && (
          <>
            <LegendControl
              open={legendVisible}
              onToggle={() => setLegendVisible(!legendVisible)}
              top={98}
            />
            {legendVisible && !isBenchmarkColorsLoading && (
              <Legend legendColors={benchmarkScaleColors} />
            )}

            <DataViz
              open={resultsPanelVisible}
              selectedMonitoringLocation={selectedMonitoringLocation}
              analyticsResults={selectedLocationWqSummaryData}
              benchmarkScaleColors={benchmarkScaleColors}
              filterValues={filterValues}
              isAnalyticsTableDataLoading={isWqSummaryLoading}
              timeSeriesResults={wqTimeSeriesData}
              isTimeSeriesTableDataLoading={isWqTimeSeriesLoading}
              onClose={() => setResultsPanelVisible(false)}
            />
          </>
        )}

        {eventsRegistered && isFiltersInitialized && (
          <GraphModeToggle
            open={mode === GRAPH_MODE}
            handleClick={() =>
              enterMode(mode === GRAPH_MODE ? MAP_EXPLORER_MODE : GRAPH_MODE)
            }
          />
        )}
      </MapContainer>

      <StreamflowDataDialog />

      {eventsRegistered && printReportDialogOpen && (
        <span
          style={{
            display: "none",
            width: "100%",
          }}
        >
          <PrintMapFormat
            ref={printRef}
            title={title}
            mapImg={map.getCanvas().toDataURL("image/png")}
            map={map}
            shareUrl={getShareUrl()}
          />
        </span>
      )}
    </>
  );
};

export default PublicMapContent;
